/* LeadsTab — Master Admin pricing-page leads inbox.
 *
 * Mounted on the new "Leads" tab in MasterAdminPage. Lists submissions
 * from /contact-sales (persisted as `sales_leads` in Mongo) and lets
 * the operator move them through a tiny pipeline (new → contacted →
 * qualified → won / lost / archived) with optional internal notes.
 *
 * Design choices:
 *  - Single endpoint per render (GET /api/master/leads) returns rows +
 *    counts, so the filter chips don't need a second roundtrip.
 *  - Status updates are optimistic — we flip the row immediately and
 *    only roll back on error.
 *  - The status update endpoint accepts an optional `notes` field which
 *    is appended to a `notes_log` array on the lead (audit trail).
 *  - Row expansion shows the full message + notes history — keeps the
 *    table dense by default.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Inbox, ChevronDown, ChevronRight, Mail, Building2, Users, Calendar, Loader2, MessageSquare, ExternalLink, Tag,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display metadata for each pipeline status. Order also drives the
// filter-chip row above the table.
const STATUSES = [
  { id: 'new',       label: 'New',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', pillCls: 'bg-emerald-500' },
  { id: 'contacted', label: 'Contacted',  cls: 'bg-sky-50 text-sky-700 border-sky-200',             pillCls: 'bg-sky-500' },
  { id: 'qualified', label: 'Qualified',  cls: 'bg-amber-50 text-amber-700 border-amber-200',       pillCls: 'bg-amber-500' },
  { id: 'won',       label: 'Won',        cls: 'bg-green-100 text-green-800 border-green-300',      pillCls: 'bg-green-600' },
  { id: 'lost',      label: 'Lost',       cls: 'bg-red-50 text-red-700 border-red-200',             pillCls: 'bg-red-500' },
  { id: 'archived',  label: 'Archived',   cls: 'bg-slate-50 text-slate-600 border-slate-200',       pillCls: 'bg-slate-400' },
];

const PLAN_LABEL = {
  forms: 'Starter',
  documents: 'Pro',
  complete: 'Compliance',
  enterprise: 'Enterprise',
};

function StatusBadge({ status }) {
  const meta = STATUSES.find(s => s.id === status) || { label: status, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.pillCls || 'bg-slate-400'}`}></span>
      {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function LeadRow({ lead, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState(lead.status);
  const [saving, setSaving] = useState(false);

  const save = async (newStatus) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/master/leads/${lead.id}/status`, {
        status: newStatus,
        notes: note.trim() || null,
      });
      setNote('');
      onStatusChange(lead.id, newStatus, note.trim() || null);
      toast.success(`Marked as ${newStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update lead');
      setPendingStatus(lead.status);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <tr
        className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
        data-testid={`lead-row-${lead.id}`}
      >
        <td className="px-3 py-3 text-slate-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-3 font-medium text-slate-900">{lead.company || '—'}</td>
        <td className="px-3 py-3 text-slate-700">{lead.name || <span className="text-slate-400 italic">no name</span>}</td>
        <td className="px-3 py-3">
          <a href={`mailto:${lead.email}`} className="text-emerald-700 hover:underline" onClick={(e) => e.stopPropagation()}>
            {lead.email}
          </a>
        </td>
        <td className="px-3 py-3 text-slate-700">{lead.recruiters || '—'}</td>
        <td className="px-3 py-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">
            <Tag className="w-3 h-3" /> {PLAN_LABEL[lead.plan] || lead.plan || '—'}
          </span>
        </td>
        <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(lead.created_at)}</td>
        <td className="px-3 py-3"><StatusBadge status={lead.status} /></td>
      </tr>

      {expanded && (
        <tr className="bg-slate-50/40 border-t border-slate-100" data-testid={`lead-expand-${lead.id}`}>
          <td colSpan={8} className="px-6 py-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: prospect details */}
              <div className="space-y-3 lg:col-span-2">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Prospect message
                </h4>
                <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700 whitespace-pre-wrap">
                  {lead.message || <span className="text-slate-400 italic">No message provided.</span>}
                </div>

                {lead.notes_log && lead.notes_log.length > 0 && (
                  <>
                    <h4 className="text-sm font-semibold text-slate-900 pt-2">Notes history</h4>
                    <ul className="space-y-2">
                      {lead.notes_log.map((n, i) => (
                        <li key={i} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>{n.by} · {fmtDate(n.at)}</span>
                            <StatusBadge status={n.status} />
                          </div>
                          <p className="text-slate-700">{n.note}</p>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              {/* Right: status changer + note */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-900">Update pipeline</h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {STATUSES.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPendingStatus(s.id); }}
                      className={`text-xs px-2 py-1.5 rounded-md border font-medium transition ${
                        pendingStatus === s.id ? s.cls + ' ring-1 ring-current' : 'border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`lead-status-${s.id}-${lead.id}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Add a note (optional) — e.g. 'Booked demo for Tuesday'"
                  className="w-full text-sm rounded-md border border-slate-200 p-2 min-h-[68px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid={`lead-note-${lead.id}`}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={(e) => { e.stopPropagation(); save(pendingStatus); }}
                    disabled={saving || (pendingStatus === lead.status && !note.trim())}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`lead-save-${lead.id}`}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                  </Button>
                  <a
                    href={`mailto:${lead.email}?subject=Re:%20Your%20HyreDocs%20trial%20enquiry`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant="outline" className="text-xs" data-testid={`lead-email-${lead.id}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Email
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function LeadsTab() {
  // `state` consolidates rows + counts + loading so we only ever call
  // setState inside the fetch promise — keeps React 19's
  // `set-state-in-effect` rule happy.
  const [state, setState] = useState({ leads: [], counts: {}, loading: true });
  const [filter, setFilter] = useState(null); // null = all
  const [search, setSearch] = useState('');

  // Fetch leads. `statusFilter` is `null` for All. Returns a fresh
  // snapshot so consumers can refresh after an action.
  const fetchLeads = async (statusFilter) => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    const res = await axios.get(`${API_URL}/master/leads${params}`);
    return res.data;
  };

  useEffect(() => {
    let cancelled = false;
    fetchLeads(filter)
      .then(data => {
        if (cancelled) return;
        setState({ leads: data.leads || [], counts: data.counts || {}, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Failed to load leads');
        setState(prev => ({ ...prev, loading: false }));
      });
    return () => { cancelled = true; };
  }, [filter]);

  const { leads, counts, loading } = state;

  // Client-side text search across the loaded rows. Keeps server roundtrips
  // off the critical path — leads volume will stay manageable for v1.
  const filteredLeads = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter(l =>
      (l.company || '').toLowerCase().includes(q) ||
      (l.name || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q) ||
      (l.message || '').toLowerCase().includes(q)
    );
  }, [leads, search]);

  // Optimistic-update helper used by each row.
  const onStatusChange = (id, newStatus, newNote) => {
    setState(prev => {
      const updatedLeads = prev.leads.map(l => {
        if (l.id !== id) return l;
        const updatedLog = newNote
          ? [...(l.notes_log || []), { at: new Date().toISOString(), by: 'you', status: newStatus, note: newNote }]
          : l.notes_log;
        return { ...l, status: newStatus, notes_log: updatedLog };
      });
      return { ...prev, leads: updatedLeads };
    });
    // When a specific filter is active a status change may move the
    // row out of view — refresh once to keep counts/visibility honest.
    if (filter) {
      fetchLeads(filter)
        .then(data => setState(prev => ({ ...prev, leads: data.leads || [], counts: data.counts || {} })))
        .catch(e => console.warn('Lead filter error:', e?.message));
    }
  };

  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0);

  return (
    <div className="space-y-5" data-testid="leads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="w-5 h-5 text-emerald-600" /> Leads
          </h2>
          <p className="text-sm text-slate-600 mt-0.5">
            Submissions from the public <code className="text-xs">/contact-sales</code> form. {totalAll} total.
          </p>
        </div>
        <Input
          placeholder="Search agency, name, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
          data-testid="leads-search"
        />
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="leads-filter-chips">
        <button
          type="button"
          onClick={() => setFilter(null)}
          className={`text-xs px-3 py-1.5 rounded-full border transition ${
            filter === null ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
          data-testid="leads-filter-all"
        >
          All <span className="opacity-60 ml-1">{totalAll}</span>
        </button>
        {STATUSES.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => setFilter(s.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${
              filter === s.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`leads-filter-${s.id}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${s.pillCls}`}></span>
            {s.label}
            <span className="opacity-60 ml-0.5">{counts[s.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading leads…
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="text-center py-12 px-6">
            <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-700">
              {search ? 'No leads match your search' : filter ? `No ${filter} leads` : 'No leads yet'}
            </h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              {search || filter
                ? 'Try clearing the filter or search.'
                : 'When someone fills out the trial form on /contact-sales, it lands here and an email fires to hello@hyredocs.com.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> Agency</span></th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span></th>
                <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> Recruiters</span></th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2"><span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Received</span></th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => (
                <LeadRow key={lead.id || lead.email + lead.created_at} lead={lead} onStatusChange={onStatusChange} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
